/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { empty, from, EMPTY } from 'rxjs';
import { tap, map, mergeMap } from 'rxjs/operators';

import { fetch } from '../service_broker/fetch';
import { resolveRestApi } from '../router/app-router';
import { logAndCatchError } from '../utils/rxjs-utils';
import {
  addActionsToParentContext,
  getActionPayload,
  processHeaders,
  processViewResponse,
  SCOPE_TYPES,
  getDirtyDetails
} from '../store/state/epics/util';
import {
  ACTION_OPEN_PROCESS_ACTION,
  ACTION_OPEN_CASE_WIDE_LOCAL_ACTION,
  ACTION_OPEN_LOCAL_ACTION,
  ACTION_OPEN_BULK_ACTION
} from '../store/state/actions';
import { ActionManagerInstance } from '../actions/manager';
import { dispatch } from '../store/state/state-manager';
import { getResolvedTargetName } from '../router/routing-manager';
import { buildContextName, buildModalContext } from '../utils/context-utils';
import constants, { publicConstants } from '../constants';
import { getTranslatedDXAPIResponse } from '../utils/dxapi-response-translator';
import * as actionType from '../store/state/actions';
import * as actions from '../store/state/actions/action-creators';
import {
  getDataContextName,
  hasContainerItems,
  isContainerMultipleMode,
  getContainerItemName,
  isContainerItemExists,
  getChildContainers,
  getActiveContainerItemName,
  getTargetFromContainerItemID
} from '../container/container-utils';
import { isContextDirty } from '../store/state/middlewares/property-change-middleware';
import type { Action } from '../store/state/state-manager';

import {
  getContainerName,
  isModalContainer,
  isParentAndChildContextHoldingSameItem,
  updateKeyAndFlowName,
  isErrorActionType,
  deferCaseSummaryViewUpdate,
  isCoexistenceCase
} from './common-helper';
import { saveAndCloseAction } from './save-and-close/action-creator';
import { saveAssignment } from './open-local-action/helper';

const { LOCAL_ACTION_TYPE } = constants;
const { RESOURCE_TYPES } = publicConstants;

export const getRouteKey = (payload: Action['payload']) => {
  const localActionType = payload.localActionType.toLowerCase();
  if (localActionType === LOCAL_ACTION_TYPE.CASE_WIDE || localActionType === LOCAL_ACTION_TYPE.EXPRESS) {
    return ACTION_OPEN_CASE_WIDE_LOCAL_ACTION;
  }

  return ACTION_OPEN_LOCAL_ACTION;
};

export const executeOpenActionRequest = (
  actionInPayload: Action['payload'],
  options: { isProcessAction?: boolean; actionMgrID?: number }
) => {
  const { actionID, assignmentID, caseID, context, openActionType, modalLoadingContainer, headers } = actionInPayload;
  const { isProcessAction } = options;
  const routeKey = isProcessAction ? ACTION_OPEN_PROCESS_ACTION : getRouteKey(actionInPayload);
  const viewType = SCOPE_TYPES.FORM;
  const { reqType } = resolveRestApi(routeKey, { caseID, ID: assignmentID, actionID, viewType });
  let { restApi } = resolveRestApi(routeKey, { caseID, ID: assignmentID, actionID, viewType });
  actionInPayload.actionType = routeKey === ACTION_OPEN_CASE_WIDE_LOCAL_ACTION ? constants.CASE : constants.ASSIGNMENT;

  // [TODO]: we have to check why route info doesn't have consideration viewType parameter
  if (restApi && !restApi.includes('viewType=')) {
    restApi = !restApi.includes('?') ? `${restApi}?viewType=${viewType}` : `${restApi}&viewType=${viewType}`;
  }
  const contextData = modalLoadingContainer?.context ?? context;

  return from(
    fetch(
      restApi,
      {
        method: reqType,
        headers
      },
      contextData
    )
  ).pipe(
    map((response) => {
      return {
        response: response.data,
        actionInPayload,
        headers: processHeaders(response.headers, '', actionInPayload.headers)
      };
    }),
    logAndCatchError(`: Error occurred during ajax call at ${openActionType} Epic : `, contextData)
  );
};

export const processOpenActionRequest = (
  actionInPayload: Action['payload'],
  options: { isProcessAction?: boolean; actionMgrID?: number }
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  // isProcessAction will be true for openProcessAction redux action type
  const { isProcessAction } = options;
  const openActionType = isProcessAction ? actionType.ACTION_OPEN_PROCESS_ACTION : actionType.ACTION_OPEN_LOCAL_ACTION;
  actionInPayload.openActionType = openActionType;
  const containerName = getContainerName(openActionType, actionInPayload);
  let target: any;
  if (containerName === 'modal') {
    target = getChildContainers(actionInPayload.contextName)[0]
      ? getChildContainers(actionInPayload.contextName)[0]
      : getTargetFromContainerItemID(actionInPayload.contextName);
    const activeContainerName = target ? getActiveContainerItemName(target) : actionInPayload.contextName;
    const dataContextName = getDataContextName(activeContainerName);

    const modalContainerName = getContainerName(actionInPayload.openActionType, actionInPayload);
    const modalTarget = getResolvedTargetName(modalContainerName, actionInPayload.context);
    const modalContext = buildModalContext(
      actionInPayload.context,
      buildContextName(modalContainerName, actionInPayload.context)
    );

    // Adds modal container in a loading state while the request for data is being resolved
    dispatch(
      actions.addContainerItem({
        target: modalTarget,
        context: modalContext,
        containerItemID: modalContext,
        key: actionInPayload.caseID,
        caseViewMode: constants.PERFORM_MODE,
        actionName: actionInPayload.name,
        showProgress: true,
        root: {
          type: 'reference',
          config: {
            context: constants.CASE_INFO.CASE_INFO_CONTENT,
            type: 'view'
          }
        },
        data: {
          key: actionInPayload.caseID,
          caseInfo: {
            ID: actionInPayload.caseID
          }
        },
        headers: { ...actionInPayload.headers }
      })
    );
    dispatch(actions.setLoading({ context: modalContext }));
    actionInPayload.modalLoadingContainer = { target: modalTarget, context: modalContext };

    if (!getChildContainers(actionInPayload.contextName)[0]) {
      return executeOpenActionRequest(actionInPayload, options);
    }
    if (!actionInPayload.continueExecution) {
      return executeOpenActionRequest(actionInPayload, options);
    }
    if (!isContextDirty(dataContextName)) {
      return executeOpenActionRequest(actionInPayload, options);
    }
    actionInPayload.saveActionPayload.modalLoadingContainer = actionInPayload.modalLoadingContainer;
    actionInPayload.saveActionPayload.canUpdateAvailableActions = !actionInPayload.saveActionPayload.isModalAction;
    const saveAction = saveAndCloseAction(actionInPayload.saveActionPayload);
    return from([saveAction]).pipe(
      mergeMap((savePayload) => {
        return saveAssignment(savePayload);
      }),
      mergeMap((actionsOut) => {
        if (actionsOut.length === 1 && isErrorActionType(actionsOut[0].type)) {
          const { continueExecution } = getDirtyDetails(target);
          if (!continueExecution) {
            ActionManagerInstance.rejectActionByID(actionInPayload.actionMgrID);
            return EMPTY.pipe(
              tap(() => {
                return empty();
              })
            ).toPromise();
          }
          if (continueExecution) {
            return executeOpenActionRequest(actionInPayload, options);
          }
        }
        return executeOpenActionRequest(actionInPayload, options);
      }),
      map((responseData) => {
        return responseData;
      })
    );
  }
  return executeOpenActionRequest(actionInPayload, options);
};

export const deriveActionButtonMethod = (mode: string) => {
  return mode === 'async' ? 'submitBulkActionAsync' : 'submitBulkAction';
};

export const postProcessOpenActionRequest = async (
  response: any,
  actionsOut: Action[],
  { headers, actionInPayload }: { headers: any; actionInPayload: Action['payload'] }
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const {
    context: actionInContext,
    actionName,
    openActionType,
    doDeleteCaseSummary,
    modalLoadingContainer
  } = actionInPayload;
  const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
  const containerName = getContainerName(openActionType, actionInPayload);
  const target = modalLoadingContainer?.target ?? getResolvedTargetName(containerName, actionInContext);
  let containerItemID;
  let context;
  let isNewContainerItem = false;

  // build new context, containetItemID if the target doesn't have any containers
  // or always new for multiple mode target
  // or always new context, containetItemID for modal container
  if (
    !hasContainerItems(target) ||
    (isContainerMultipleMode(target) && !isContainerItemExists(target, response.data.caseInfo.ID))
  ) {
    context = buildContextName(containerName, actionInContext);
    if (isModalContainer(containerName)) {
      context = buildModalContext(actionInContext, context);
    }
    containerItemID = context;
    isNewContainerItem = true;
  } else {
    if (isContainerMultipleMode(target)) {
      containerItemID = getContainerItemName(target, response.data.caseInfo.ID);
    } else containerItemID = getActiveContainerItemName(target);
    context = getDataContextName(containerItemID);
  }

  /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
  const options = { acName: target, target, containerName, context };
  await processViewResponse(translatedDXAPIResponse, actionInPayload, options);

  if (
    translatedDXAPIResponse?.data?.caseInfo?.content === undefined ||
    (translatedDXAPIResponse.data.caseInfo.content !== undefined &&
      typeof translatedDXAPIResponse.data.caseInfo.content === 'object' &&
      Object.keys(translatedDXAPIResponse.data.caseInfo.content).length === 0)
  ) {
    delete translatedDXAPIResponse.data?.caseMessages;
  }
  const rootViewInfo = translatedDXAPIResponse.uiResources?.root;
  let caseViewMode;
  if (rootViewInfo) {
    caseViewMode = constants.PERFORM_MODE;
  } else {
    caseViewMode = constants.REVIEW_MODE;
  }
  const payload = getActionPayload(translatedDXAPIResponse, {
    target,
    context,
    containerItemID,
    caseViewMode,
    doDeleteCaseSummary,
    headers,
    actionName,
    isMinimizable: false,
    isInCreateStage: isModalContainer(containerName), // to suppress the local actions buttons coming in Modal > Assignment
    resourceType: RESOURCE_TYPES.ASSIGNMENT,
    isProcessAction: actionInPayload.isProcessAction,
    showProgress: actionInPayload.showProgress,
    progressMessage: actionInPayload.progressMessage,
    assignmentID: actionInPayload.assignmentID,
    actionType: actionInPayload.actionType
  });
  // updating key and flowName
  if (rootViewInfo) {
    updateKeyAndFlowName(payload, translatedDXAPIResponse);
  }

  // deleting doDeleteCaseSummary as it is not required during modal container case
  if (isModalContainer(containerName)) delete payload.doDeleteCaseSummary;

  if (actionInPayload.openActionType === ACTION_OPEN_BULK_ACTION) {
    payload.isBulkAction = true;
    // TODO - Need to remove this after integrating actual Dx api

    payload.data.caseInfo.actionButtons.main[0].jsAction = deriveActionButtonMethod(actionInPayload.runningMode);
    payload.data.caseInfo.actionButtons.secondary[0].jsAction = 'cancelBulkAction';
    payload.data.caseInfo.cases = actionInPayload.selectedList;
  }

  let action;
  if (isNewContainerItem) {
    action = actions.addContainerItem;
  } else if (actionInPayload.isProcessAction) {
    action = actions.replaceContainerItem;
  } else {
    action = actions.updateContainerItem;
  }
  payload.data.context_data.isProcessAction = actionInPayload.isProcessAction;
  actionsOut.push(actions.postProcess(action, payload));

  if (!isNewContainerItem) {
    actionsOut.push(actions.postProcess(actions.resetLoading, payload));
  }
  /**
   * Use case : Launch local action and perform cancel
   * To merge latest response headers(eg: eTag) to respective workarea.
   */
  if (isNewContainerItem) {
    const containersList = getChildContainers(actionInPayload.contextName);
    // @ts-expect-error
    const activeContainerName = getContainerItemName(containersList, response.data.caseInfo.ID);
    if (activeContainerName) {
      const parentPayload = {
        data: {
          caseInfo: {
            headers
          }
        },
        context: activeContainerName
      };
      actionsOut.push(actions.postProcess(actions.mergeData, parentPayload));
    }
  }

  if (containerItemID !== getActiveContainerItemName(target)) {
    actionsOut.push(actions.postProcess(actions.activateContainerItem, { target, containerItemID }));
  }
  if (isParentAndChildContextHoldingSameItem(containerItemID) && !modalLoadingContainer?.context) {
    addActionsToParentContext(payload, actionsOut);
  }

  if (actionInPayload.isProcessAction && !isCoexistenceCase(payload.nextAssignmentInfo?.className)) {
    deferCaseSummaryViewUpdate(
      { response: translatedDXAPIResponse, actionInPayload: { ...actionInPayload, containerItemID } },
      actionsOut
    );
  }

  // action to remove previous container when case is in review mode after launching the optional process action.
  if (payload.caseViewMode === constants.REVIEW_MODE) {
    actionsOut.push(actions.postProcess(actions.removeContainerItem, payload));
  }
  return actionsOut;
};
